import * as React from 'react'
import styled, { css } from 'styled-components'
import { Section, SectionContent, SectionCta } from '../../Section'
import useRealtimeDataTokenStream from '~hooks/useRealtimeDataTokenStream'
import formatMoney from '~utils/formatMoney'
import { Mono, MqDesktop, MqTablet, Regular, UltraLight } from 'streamr-ui/css/consts'
import CmcLogo from '../assets/cmc_logo.svg'
import { opacityPulseKeyframes } from '../../../utils/css'

export function Metrics() {
    const metrics = useRealtimeDataTokenStream()

    const {
        DATAUSDPoint = 0,
        DATAUSDVolumePoint = 0,
        DATAUSDMarketCapPoint = 0,
        circulatingSupply = 0,
        totalSupply = 0,
    } = metrics || {}

    const isLoadingMetrics = !metrics

    return (
        <MetricsRoot>
            <SectionContent>
                <Content>
                    <Monetary $appearIntersection>
                        <div>
                            <Name $appear>DATA/USD</Name>
                            <Value $appear>
                                <ValueDisplay isReady={!isLoadingMetrics}>
                                    {formatMoney(Math.round(DATAUSDPoint * 10000) / 10000).replace(
                                        /\s+/g,
                                        ',',
                                    )}
                                </ValueDisplay>
                            </Value>
                        </div>
                        <div>
                            <Name $appear>24H CEX VOLUME/USD</Name>
                            <Value $appear>
                                <ValueDisplay isReady={!isLoadingMetrics}>
                                    {formatMoney(Math.round(DATAUSDVolumePoint)).replace(
                                        /\s+/g,
                                        ',',
                                    )}
                                </ValueDisplay>
                            </Value>
                        </div>
                        <div>
                            <Name $appear>MARKET CAP/USD</Name>
                            <Value $appear>
                                <ValueDisplay isReady={!isLoadingMetrics}>
                                    {formatMoney(Math.round(DATAUSDMarketCapPoint)).replace(
                                        /\s+/g,
                                        ',',
                                    )}
                                </ValueDisplay>
                            </Value>
                        </div>
                    </Monetary>
                    <Supply $appearIntersection>
                        <div>
                            <Name $appear>TOTAL SUPPLY</Name>
                            <Value $appear>
                                <ValueDisplay isReady={!isLoadingMetrics}>
                                    {formatAmount(totalSupply)}
                                </ValueDisplay>
                            </Value>
                        </div>
                        <div>
                            <Name $appear>MAXIMUM SUPPLY</Name>
                            <Value $appear>
                                <ValueDisplay isReady={!isLoadingMetrics}>2B</ValueDisplay>
                            </Value>
                        </div>
                        <div>
                            <Name $appear>CIRCULATING SUPPLY</Name>
                            <Value $appear>
                                <ValueDisplay isReady={!isLoadingMetrics}>
                                    {formatAmount(circulatingSupply)}
                                </ValueDisplay>
                            </Value>
                        </div>
                    </Supply>
                </Content>
                <SectionCta
                    as="a"
                    href="https://coinmarketcap.com/currencies/streamr/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <CmcLabel>
                        <div>
                            <img src={CmcLogo} alt="CMC" />
                        </div>
                        <div>Streamr on CoinMarketCap</div>
                    </CmcLabel>
                    <div>
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 13 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M2 1H12V11"
                                stroke="#0324FF"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <line
                                x1="11.75"
                                y1="1.0606"
                                x2="1.06049"
                                y2="11.988"
                                stroke="#0324FF"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                </SectionCta>
            </SectionContent>
        </MetricsRoot>
    )
}

function ValueDisplay({ isReady = false, children }) {
    return <ValueDisplayRoot $pulse={!isReady}>{isReady ? children : <>-</>}</ValueDisplayRoot>
}

const ValueDisplayRoot = styled.div`
    ${({ $pulse = false }) =>
        $pulse &&
        css`
            animation: ${opacityPulseKeyframes} 1s infinite;
        `}
`

const TwoColumnLayoutMq = '(min-width: 480px)'

const CmcLabel = styled.div`
    align-items: center;
    color: #0324ff !important;
    display: flex;
    flex-grow: 1;
    font-size: 16px;
    font-weight: 500;
    gap: 16px;
    line-height: 30px;

    img {
        display: block;
        height: 36px;
        width: 36px;
    }
`

const Wing = styled.div`
    > div + div {
        margin-top: 32px;
    }
`

const Monetary = styled(Wing)`
    @media ${TwoColumnLayoutMq} {
        text-align: right;
    }
`

const Name = styled.div`
    font-family: ${Mono};
    font-size: 0.625rem;
    font-weight: ${Regular};
    letter-spacing: 1.5px;
    line-height: 1.5rem;
    text-transform: uppercase;
    color: #adadad;
    white-space: nowrap;

    @media ${MqTablet} {
        font-size: 0.875rem;
    }
`

const Value = styled.div`
    font-size: 1.375rem;
    font-weight: ${UltraLight};
    line-height: 2.25rem;
    margin-top: 0;
    color: #0c009a;
    letter-spacing: -0.5px;
    white-space: nowrap;

    @media ${MqTablet} {
        margin-top: 0.5rem;
        font-size: 2rem;
    }

    @media ${MqDesktop} {
        font-size: 2.5rem;
    }
`

const Supply = styled(Wing)``

const Content = styled.div`
    display: grid;
    gap: 32px;

    @media ${TwoColumnLayoutMq} {
        gap: 4rem;
        grid-template-columns: 1fr 1fr;
    }
`

const MetricsRoot = styled(Section)`
    --SectionCta_Padding: 20px;
    --SectionCta_Margin: 80px auto 0;
    --SectionCta_Width: 480px;

    padding-top: var(--defaultPadding) !important;
`

const formatAmount = (amount) => {
    if (amount > 1000000000) {
        return `${(Math.floor((100 * amount) / 1000000000) / 100).toFixed(2)}B`
    }

    if (amount > 1000000) {
        return `${Math.round(amount / 1000000)}M`
    }

    if (amount > 1000) {
        return `${Math.round(amount / 1000)}K`
    }

    return Math.round(amount / 1000)
}
